import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { ArrowLeft, Clock, Heart, HeartOff, Users } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useFavorites } from "../hooks/useFavorites";
import {
  type Recipe,
  type RecipeDetails,
  type RecipeIngredient,
  type RecipeStep,
  recipeService,
} from "../services/recipeService";
import { formatAmount } from "../utils/formatAmount";

// Display detailed information for a single recipe and manage loading state
// Fetches recipe details on mount and renders ingredients, instructions, and source link when available
// When `preloadedDetails` is provided (e.g. from a saved favorite), no API call is made

function BackButton({
  onBack,
  className = "",
}: {
  onBack: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onBack}
      className={`flex items-center gap-2 font-medium text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 ${className}`}
      aria-label="Back to recipe search results"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      Back to Recipes
    </button>
  );
}

interface RecipeResultsProps {
  recipe: Recipe;
  onBack: () => void;
  preloadedDetails?: RecipeDetails;
}

export default function RecipeResults({
  recipe,
  onBack,
  preloadedDetails,
}: RecipeResultsProps) {
  const [details, setDetails] = useState<RecipeDetails | null>(
    preloadedDetails ?? null,
  );
  const [loading, setLoading] = useState(!preloadedDetails);
  const { isFavorited, getFavoriteId, addFavorite, removeFavorite, isAdding } =
    useFavorites();

  // Intercept the hardware/gesture back button on native so it dismisses the
  // detail view instead of navigating away from the recipes tab entirely.
  // On native (Capacitor) we use App.addListener which fires before the
  // WebView ever sees the event, so TanStack Router's history is never touched.
  // On web we fall back to a no-op (the UI Back button is the only back path).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cleanup: (() => void) | undefined;
    App.addListener("backButton", () => {
      onBack();
    }).then((handle) => {
      cleanup = () => handle.remove();
    });

    return () => {
      cleanup?.();
    };
  }, [onBack]);

  useEffect(() => {
    // Skip the API call when details were supplied by the caller (e.g. from favorites)
    if (preloadedDetails) return;

    let cancelled = false;

    // 300 ms debounce — if the user navigates away before this fires,
    // the timeout is cleared and no Edge Function invocation is made.
    const timer = setTimeout(async () => {
      if (cancelled) return;
      try {
        const data = await recipeService.getRecipeDetails(recipe.id);
        if (!cancelled) {
          setDetails(data);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("🔴 Failed to load recipe details:", error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [recipe.id, preloadedDetails]);

  const favorited = isFavorited(recipe.id);
  const favoriteId = getFavoriteId(recipe.id);

  const handleToggleFavorite = async () => {
    try {
      if (favorited && favoriteId) {
        await removeFavorite(favoriteId);
        toast("Removed from favorites");
      } else if (details) {
        await addFavorite({ recipe, details });
        toast.success("Added to favorites!");
      }
    } catch (err) {
      console.error("🔴 Failed to update favorites:", err);
      toast.error("Failed to update favorites");
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <BackButton onBack={onBack} className="mb-4" />
        <output className="flex justify-center py-12" aria-live="polite">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-green-200 border-t-green-600 dark:border-green-700 dark:border-t-green-400"
            aria-hidden="true"
          ></div>
          <span className="sr-only">Loading recipe details...</span>
        </output>
      </div>
    );
  }

  if (!details) {
    return (
      <div className="space-y-6">
        <BackButton onBack={onBack} className="mb-4" />
        <div
          className="py-8 text-center text-gray-500 dark:text-gray-400"
          role="alert"
        >
          <p>Failed to load recipe details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackButton onBack={onBack} />

      <article className="overflow-hidden rounded-lg bg-white shadow-sm dark:bg-gray-800">
        <img src={recipe.image} alt="" className="h-96 w-full object-cover" />

        <div className="p-6">
          <h1 className="mb-6 text-3xl font-bold text-gray-800 dark:text-gray-100">
            {recipe.title}
          </h1>

          <div className="mb-6 grid grid-cols-2 gap-4">
            {details.readyInMinutes && (
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <Clock
                  className="h-5 w-5 text-yellow-600 dark:text-yellow-400"
                  aria-hidden="true"
                />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Ready in
                  </p>
                  <p className="font-semibold">
                    {details.readyInMinutes} minutes
                  </p>
                </div>
              </div>
            )}
            {details.servings && (
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                <Users
                  className="h-5 w-5 text-blue-500 dark:text-blue-400"
                  aria-hidden="true"
                />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Servings
                  </p>
                  <p className="font-semibold">{details.servings}</p>
                </div>
              </div>
            )}
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <section>
              <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
                Ingredients
              </h2>
              <ul className="space-y-2">
                {details.extendedIngredients?.map(
                  (ingredient: RecipeIngredient) => (
                    <li
                      key={`${ingredient.id}-${ingredient.original}`}
                      className="flex gap-3 text-gray-700 dark:text-gray-300"
                    >
                      <span
                        className="min-w-fit font-medium text-green-600 dark:text-green-400"
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: safe because value comes from a number, not user input
                        dangerouslySetInnerHTML={{
                          __html: `${formatAmount(ingredient.measures?.us?.amount ?? 0) || ingredient.original?.match(/\d+/)?.[0] || "1"} ${ingredient.measures?.us?.unitShort || "unit"}`,
                        }}
                      />
                      <span>{ingredient.name}</span>
                    </li>
                  ),
                )}
              </ul>
            </section>

            <section>
              <h2 className="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">
                Instructions
              </h2>
              {details.analyzedInstructions?.[0]?.steps ? (
                <ol className="space-y-3">
                  {details.analyzedInstructions[0].steps.map(
                    (step: RecipeStep, index: number) => (
                      <li key={step.number} className="flex gap-3">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-600 text-sm font-medium text-white dark:bg-green-600">
                          {index + 1}
                        </span>
                        <span className="pt-0.5 text-gray-700 dark:text-gray-300">
                          {step.step}
                        </span>
                      </li>
                    ),
                  )}
                </ol>
              ) : (
                <p className="text-gray-600 italic dark:text-gray-400">
                  {details.instructions || "No instructions available"}
                </p>
              )}
            </section>
          </div>

          <div className="mt-6 flex flex-wrap gap-3 border-t border-gray-200 pt-6 dark:border-gray-700">
            {details.sourceUrl && (
              <a
                href={details.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block rounded-lg bg-green-600 px-6 py-2 font-medium text-white hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700"
              >
                View Full Recipe
              </a>
            )}
            <button
              type="button"
              onClick={handleToggleFavorite}
              disabled={isAdding}
              aria-label={
                favorited ? "Remove from favorites" : "Add to favorites"
              }
              aria-pressed={favorited}
              className={`inline-flex items-center gap-2 rounded-lg px-6 py-2 font-medium transition ${
                favorited
                  ? "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  : "bg-pink-100 text-pink-700 hover:bg-pink-200 dark:bg-pink-900/40 dark:text-pink-300 dark:hover:bg-pink-900/60"
              } disabled:pointer-events-none disabled:opacity-50`}
            >
              {favorited ? (
                <HeartOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Heart
                  className="h-4 w-4 fill-pink-600 text-pink-600 dark:fill-pink-400 dark:text-pink-400"
                  aria-hidden="true"
                />
              )}
              {favorited ? "Remove Favorite" : "Add to Favorites"}
            </button>
          </div>
        </div>
      </article>
    </div>
  );
}
